import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useAtom } from 'jotai';
import { enqueueSnackbar } from 'notistack';

import useHandleError from '@/hooks/useHandleError';
import { selectedOrganizationAtom } from '@/pages/Organizations';
import { axiosClient } from '@/services/BaseApi.service';
import userAtom from '@/store/user.store';
import type { UserInfo } from '@/types/common';

const useUserInfo = () => {
  const handleError = useHandleError();
  const [user, setUser] = useAtom(userAtom);
  const queryClient = useQueryClient();
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);

  const resetMfa = useMutation({
    mutationFn: () => axiosClient.post(`/users/${user?.id}/reset_mfa`),
    onSuccess: () => {
      enqueueSnackbar(`MFA reset`, {
        variant: 'success'
      });
    },
    onError: (error: AxiosError) => {
      handleError(error, `Failure to reset MFA`);
    }
  });

  const updateProfile = useMutation({
    mutationFn: (data: {
      name: string;
      phone_number: string | null;
      phone_extension: string | null;
      office_phone_number: string | null;
      office_phone_extension: string | null;
      sms_consent: boolean;
      tos_consent: boolean;
    }) => axiosClient.put(`/users/profile`, data),
    onSuccess: (res) => {
      setUser({
        ...user,
        name: res.data.name,
        phone_number: res.data.phone_number,
        phone_extension: res.data.phone_extension,
        office_phone_number: res.data.office_phone_number,
        office_phone_extension: res.data.office_phone_extension,
        sms_consent: res.data.sms_consent,
        tos_consent: res.data.tos_consent
      } as UserInfo);
      enqueueSnackbar('Profile updated', { variant: 'success' });
      if (selectedOrganization.label !== 'all') {
        queryClient.invalidateQueries({
          queryKey: [
            `organizations/${selectedOrganization?.label}/users?exclude_service_accounts=true`
          ]
        });
      }
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to update profile');
    }
  });

  return {
    resetMfa,
    updateProfile
  };
};

export default useUserInfo;
